import { stripComposerInvisibleChars } from '../projects/projects-composer-editor-draft';

/** Follow-up sent inside an existing Work/project chat to capture a workflow. */
export const MAKE_AS_WORKFLOW_FOLLOW_UP_PROMPT =
  'Turn what we covered in this chat into a reusable routine. Review our conversation, extract the repeatable steps, and help me create a routine I can run again on demand or on a schedule. Do not include any patient-specific details, names, identifiers, or clinical facts from this chat in the routine instructions — keep them generalized and reusable.';

/** Plain-text sentence shown in the composer and user bubble. */
export const MAKE_AS_WORKFLOW_DISPLAY_LABEL = 'Turn this chat into a reusable routine';

/**
 * Retired display label. Historical messages were persisted with this exact
 * text — kept only as a match key for `isMakeAsWorkflowDisplayText` so those
 * messages keep rendering as the (new) short sentence instead of falling
 * through to raw wire text. Never shown to a user going forward.
 */
const LEGACY_MAKE_AS_WORKFLOW_DISPLAY_LABEL = 'Make as routine';

/** Every display string `isMakeAsWorkflowDisplayText` must recognize. */
const KNOWN_MAKE_AS_WORKFLOW_DISPLAY_LABELS = [
  MAKE_AS_WORKFLOW_DISPLAY_LABEL,
  LEGACY_MAKE_AS_WORKFLOW_DISPLAY_LABEL,
];

/** Follow-up to fold chat edits into the routine this chat already belongs to. */
export const UPDATE_WORKFLOW_FOLLOW_UP_PROMPT =
  'Update the routine we just ran with the durable improvements from this chat. Review what changed, fold those reusable steps into the existing routine instructions so future runs remember them, and leave patient-specific details out.';

/** Plain-text sentence for updating an existing routine from chat. */
export const UPDATE_WORKFLOW_DISPLAY_LABEL = 'Update this routine with what changed here';

/**
 * Retired display label. Historical messages were persisted with this exact
 * text — kept only as a match key for `isUpdateWorkflowDisplayText` so those
 * messages keep rendering as the (new) short sentence instead of falling
 * through to raw wire text. Never shown to a user going forward.
 */
const LEGACY_UPDATE_WORKFLOW_DISPLAY_LABEL = 'Update routine';

/** Every display string `isUpdateWorkflowDisplayText` must recognize. */
const KNOWN_UPDATE_WORKFLOW_DISPLAY_LABELS = [
  UPDATE_WORKFLOW_DISPLAY_LABEL,
  LEGACY_UPDATE_WORKFLOW_DISPLAY_LABEL,
];

export type MakeAsWorkflowSubmit = {
  readonly wire: string;
  readonly display: string;
};

const normalizeMakeAsWorkflowText = (text: string): string =>
  stripComposerInvisibleChars(text).trim();

export const isMakeAsWorkflowWireText = (text: string | undefined): boolean =>
  normalizeMakeAsWorkflowText(text ?? '') === MAKE_AS_WORKFLOW_FOLLOW_UP_PROMPT;

export const isMakeAsWorkflowDisplayText = (text: string | undefined): boolean =>
  KNOWN_MAKE_AS_WORKFLOW_DISPLAY_LABELS.includes(normalizeMakeAsWorkflowText(text ?? ''));

export const isUpdateWorkflowWireText = (text: string | undefined): boolean =>
  normalizeMakeAsWorkflowText(text ?? '') === UPDATE_WORKFLOW_FOLLOW_UP_PROMPT;

export const isUpdateWorkflowDisplayText = (text: string | undefined): boolean =>
  KNOWN_UPDATE_WORKFLOW_DISPLAY_LABELS.includes(normalizeMakeAsWorkflowText(text ?? ''));

export const isMakeAsWorkflowUserText = (text: string | undefined): boolean =>
  isMakeAsWorkflowWireText(text) || isMakeAsWorkflowDisplayText(text);

export const isUpdateWorkflowUserText = (text: string | undefined): boolean =>
  isUpdateWorkflowWireText(text) || isUpdateWorkflowDisplayText(text);

/** Map composer/bubble text to wire + display prompts when sending. */
export const resolveMakeAsWorkflowSubmit = (
  text: string
): MakeAsWorkflowSubmit | null => {
  if (isUpdateWorkflowUserText(text)) {
    return {
      wire: UPDATE_WORKFLOW_FOLLOW_UP_PROMPT,
      display: UPDATE_WORKFLOW_DISPLAY_LABEL,
    };
  }
  if (!isMakeAsWorkflowUserText(text)) return null;
  return {
    wire: MAKE_AS_WORKFLOW_FOLLOW_UP_PROMPT,
    display: MAKE_AS_WORKFLOW_DISPLAY_LABEL,
  };
};

/** Composer prefill + pending-prompt atom value (short label, not wire text). */
export const MAKE_AS_WORKFLOW_COMPOSER_PREFILL = MAKE_AS_WORKFLOW_DISPLAY_LABEL;

/** Composer prefill when folding edits into an existing routine. */
export const UPDATE_WORKFLOW_COMPOSER_PREFILL = UPDATE_WORKFLOW_DISPLAY_LABEL;
